import { FlexRow } from '@components/FlexRow';
import FormContainer from '@components/FormContainer';
import Colors from '@constants/Colors';
import numberUtils from '@helper/number.utils';
import JobOfferBenefitForm from '@modules/JobOffers/JobOfferDetail/components/JobOfferBenefitForm';
import { Box, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

const BenefitChip = styled(Chip)(() => ({
    backgroundColor: Colors.NEUTRAL_BLACK,
    color: 'white',
    fontSize: '14px',
    fontWeight: 400,
    borderRadius: '16px',
    height: 'auto',
    '& .MuiChip-label': {
        padding: '12px 13px',
        whiteSpace: 'normal',
        lineHeight: '1.4'
    }
}));

const contractTabFields = ['contractType', 'status', 'startDate', 'endDate', 'hourPerWeek', 'dayPerWeek'];
const salaireTabFields = ['monthPerYear', 'dayOff', 'rtt', 'guaranteedSalary', 'commissions'];

const JobOfferBenefits = ({ pro, jobOffer, onSubmit, loading, onEditStateChange }: { pro: any, jobOffer: any, onSubmit?: () => Promise<void>, loading?: boolean, onEditStateChange?: (editing: boolean) => void }) => {
    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const setFormStateWithCallback = (state: 'view' | 'edit') => {
        setFormState(state);
        onEditStateChange?.(state === 'edit');
    };
    const [activeSection, setActiveSection] = useState<'contract' | 'salaire'>('contract');
    const { trigger, getValues, formState: { errors } } = useFormContext();
    const isFreelance = getValues('contract.status') === 'Indépendant / freelance';
    const isMobile = useMediaQuery('(max-width:600px)');

    const bonusValue = getValues('contract.bonus');
    const isSmartRecruitersAccount = [
        '673227ec566ade90b05494b7', // dev upercut
        '65466680e686c83a272cac2a', // prod provalliance
    ].includes(jobOffer?.pro?._id);

    const getBenefitsList = (): string[] => {
        const benefits: string[] = [];

        const contractType = getValues('contract.contractType');
        if (contractType) {
            benefits.push(contractType);
        }

        const monthPerYear = getValues('contract.monthPerYear');
        if (monthPerYear && !isFreelance) {
            benefits.push(`Salaire sur ${monthPerYear} mois`);
        }

        const hourPerWeek = getValues('contract.hourPerWeek');
        if (hourPerWeek) {
            benefits.push(`${hourPerWeek}H / semaine`);
        }

        const dayOff = getValues('contract.dayOff');
        if (dayOff?.number && dayOff?.unit && !isFreelance) {
            benefits.push(`Congés ${dayOff.number} ${dayOff.unit}`);
        }

        const dayPerWeek = getValues('contract.dayPerWeek');
        if (dayPerWeek && dayPerWeek.length > 0) {
            const daysText = dayPerWeek
                .map((item: string, index: number) => {
                    if (dayPerWeek.length === 1) {
                        return item;
                    }
                    if (index === dayPerWeek.length - 2) {
                        return item;
                    }
                    if (index === dayPerWeek.length - 1) {
                        return 'ou ' + item;
                    }
                    return item + ',';
                })
                .join(' ');
            benefits.push(`${daysText} jours de travail / semaine`);
        }

        const rtt = getValues('contract.rtt');
        if (rtt?.days && !isFreelance) {
            benefits.push(`RTT ${rtt.days} jours`);
        }

        return benefits;
    };

    const handleCancel = () => {
        setFormStateWithCallback('view');
    };

    const handleSubmit = async () => {
        const isValid = await trigger();
        if (!isValid) {
            const contractErrors = contractTabFields.some(k => !!(errors?.contract as any)?.[k]);
            const salaireErrors = salaireTabFields.some(k => !!(errors?.contract as any)?.[k]);
            if (activeSection === 'contract' && !contractErrors && salaireErrors) {
                setActiveSection('salaire');
            } else if (activeSection === 'salaire' && !salaireErrors && contractErrors) {
                setActiveSection('contract');
            }
            return;
        }
        await onSubmit?.();
        setFormStateWithCallback('view');
    }

    const hasBonus = getValues('contract.guaranteedSalary.max') !== getValues('contract.remuneration.max');
    const bonus = getValues('contract.remuneration.max') - getValues('contract.guaranteedSalary.max');
    const canShowBonus = getValues('contract.showBonus');
    const isAnnualSalary = getValues('contract.isAnnualSalary');
    const showSalary = getValues('contract.showSalary');
    return (
        <FormContainer
            formTitle={"Vous bénéficierez"}
            formState={formState}
            setFormState={setFormStateWithCallback}
            loading={loading}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
        >
            {formState === 'view' ? (
                <Box display="flex" flexWrap="wrap" gap="8px" alignItems="flex-start">
                    {!isSmartRecruitersAccount && getBenefitsList().map((benefit, index) => (
                        <BenefitChip
                            key={index}
                            label={benefit}
                            size="small"
                        />
                    ))}
                    {hasBonus && canShowBonus && (
                        <Stack sx={{ gap: 2, mt: 4 }}>
                            <Typography
                                fontSize={{
                                    xs: 14,
                                    md: 16,
                                }}
                            >
                                Commissions et primes <b>jusqu'à </b>
                                <strong>
                                    {isAnnualSalary
                                        ? `${numberUtils.endNumberWithK(bonus)}`
                                        : numberUtils.currencyFormatWithoutZero(bonus)}
                                </strong>
                                {bonusValue ? (
                                    <span>
                                        , calculée sur :
                                    </span>
                                ) : null}
                            </Typography>
                            <FlexRow
                                sx={{
                                    gap: 0.125,
                                    alignItems: 'center',
                                }}>
                                {showSalary ? (
                                    <>
                                        {bonusValue && (
                                            <BenefitChip
                                                label={bonusValue}
                                                size="small"
                                            />
                                        )}
                                    </>
                                ) : (
                                    <Stack
                                        sx={{
                                            padding: isMobile ? '8px 16px' : '10px 20px',
                                            backgroundColor: isMobile ? Colors.PRIMARY_PURPLE : 'white',
                                            color: isMobile ? 'white' : Colors.PRIMARY_PURPLE,
                                            borderRadius: '20px',
                                        }}>
                                        <Typography>
                                            À définir selon profil et expérience
                                        </Typography>
                                    </Stack>
                                )}
                            </FlexRow>
                        </Stack>
                    )}

                </Box>
            ) : (
                <JobOfferBenefitForm activeSection={activeSection} setActiveSection={setActiveSection} />
            )}
        </FormContainer>
    );
};

export default JobOfferBenefits;
